import { ArrowLeft, Plus, Tag } from 'lucide-react';
import { useState } from 'react';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { toast } from 'sonner';

import { useFeatureAccess } from '@/features/billing/model/use-feature-access';
import { ProFeatureLock } from '@/features/billing/ui/pro-feature-lock';
import { useCafe24Coupons, useCreateCouponEvent } from '@/features/coupon';
import { testIds } from '@/shared/testing';
import { Button } from '@/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { AppLayout } from '../-components/app-layout';

export const Route = createFileRoute('/_authenticated/$mallId/coupon/new')({
  component: CouponNewPage,
});

function CouponNewPage() {
  const { mallId } = useParams({ from: '/_authenticated/$mallId/coupon/new' });
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [cafe24CouponNo, setCafe24CouponNo] = useState('');
  const [cafe24CouponName, setCafe24CouponName] = useState('');

  const { locked } = useFeatureAccess('couponAutoIssueEnabled');
  const { data: coupons } = useCafe24Coupons({ enabled: !locked });
  const createMutation = useCreateCouponEvent();

  const handleCouponSelect = (couponNo: string) => {
    setCafe24CouponNo(couponNo);
    const selected = coupons?.data.find((c) => c.coupon_no === couponNo);
    setCafe24CouponName(selected?.coupon_name ?? '');
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error('이벤트명을 입력해주세요');
      return;
    }
    if (!cafe24CouponNo) {
      toast.error('쿠폰을 선택해주세요');
      return;
    }

    createMutation.mutate(
      {
        name: name.trim(),
        cafe24CouponNo,
        cafe24CouponName,
      },
      {
        onSuccess: (event) => {
          navigate({
            to: '/$mallId/coupon/$eventId',
            params: { mallId: mallId, eventId: event.id },
          });
        },
      },
    );
  };

  return (
    <AppLayout pageTitle="새 쿠폰 이벤트">
      <ProFeatureLock feature="couponAutoIssueEnabled">
        <div className="space-y-6 max-w-2xl mx-auto">
          {/* 헤더 */}
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" asChild>
              <Link to="/$mallId/coupon" params={{ mallId: mallId }}>
                <ArrowLeft className="h-4 w-4" />
                목록
              </Link>
            </Button>
          </div>

          {/* 기본 정보 */}
          <Card>
            <CardHeader className="border-b">
              <CardTitle className="text-sm flex items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                기본 정보
              </CardTitle>
              <CardDescription className="text-xs">
                이벤트를 생성한 뒤, 상세 페이지에서 발급 대상을 선택하여 쿠폰을 발급합니다
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="event-name" className="text-xs text-muted-foreground">
                  이벤트명
                </Label>
                <Input
                  id="event-name"
                  placeholder="예: 3월 이탈고객 리텐션"
                  className="text-sm"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid={testIds.coupon.nameInput}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="coupon-select" className="text-xs text-muted-foreground">
                  Cafe24 쿠폰 선택
                </Label>
                <Select value={cafe24CouponNo} onValueChange={handleCouponSelect}>
                  <SelectTrigger
                    id="coupon-select"
                    className="text-sm"
                    data-testid={testIds.coupon.couponSelect}
                  >
                    <SelectValue placeholder="쿠폰을 선택하세요" />
                  </SelectTrigger>
                  <SelectContent>
                    {coupons?.data.map((c) => (
                      <SelectItem key={c.coupon_no} value={c.coupon_no}>
                        {c.coupon_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Cafe24 쇼핑몰에 등록된 쿠폰 목록에서 선택합니다
                </p>
              </div>
            </CardContent>
            <CardFooter className="border-t justify-end gap-2">
              <Button variant="outline" asChild data-testid={testIds.coupon.cancelButton}>
                <Link to="/$mallId/coupon" params={{ mallId: mallId }}>
                  취소
                </Link>
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={createMutation.isPending}
                className="gap-1.5"
                data-testid={testIds.coupon.submitButton}
              >
                <Plus className="h-4 w-4" />
                {createMutation.isPending ? '생성 중...' : '이벤트 생성'}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </ProFeatureLock>
    </AppLayout>
  );
}
